import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api/client.js";
import type { WaitlistFilters, WaitlistItem } from "../api/waitlist.js";
import { downloadCsv } from "../csv.js";
import { downloadExcel } from "../excel.js";
import { asMoment } from "../format.js";
import { useLanguage } from "../i18n.js";
import "./waitlist.css";

const filters: WaitlistFilters = { status: "all" };
const PAGE_SIZE = 25;
export function Waitlist() {
  const french = useLanguage() === "fr";
  const words = french
    ? {
        refresh: "Actualiser",
        loading: "Chargement…",
        empty: "Aucune inscription.",
        search: "Rechercher",
        searchPlaceholder: "Rechercher par e-mail…",
        previous: "Précédent",
        next: "Suivant",
        page: "Page",
        total: (count: number) => `${count} inscription${count === 1 ? "" : "s"}`,
        matching: (count: number, total: number) => `${count} sur ${total} inscriptions`,
        failure: "Impossible de charger la liste.",
        consent: "Consentement aux e-mails de lancement",
        csv: "Exporter CSV",
        excel: "Exporter Excel",
        email: "E-mail",
        signedUp: "Inscription",
        signedUpAt: "Inscrit le",
        yes: "Oui",
        no: "Non",
        export: "Exporter",
        pagination: "Pagination",
        sheet: "Liste d’attente",
      }
    : {
        refresh: "Refresh",
        loading: "Loading…",
        empty: "No waitlist signups.",
        search: "Search",
        searchPlaceholder: "Search by email…",
        previous: "Previous",
        next: "Next",
        page: "Page",
        total: (count: number) => `${count} waitlist signup${count === 1 ? "" : "s"}`,
        matching: (count: number, total: number) => `${count} of ${total} signups`,
        failure: "Could not load the waitlist.",
        consent: "Launch email consent",
        csv: "Export CSV",
        excel: "Export Excel",
        email: "Email",
        signedUp: "Signed up",
        signedUpAt: "Signed up at",
        yes: "Yes",
        no: "No",
        export: "Export",
        pagination: "Pagination",
        sheet: "Launch waitlist",
      };
  const [items, setItems] = useState<WaitlistItem[]>([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(false);
  const active = useRef<AbortController | null>(null);
  const load = useCallback(async () => {
    active.current?.abort();
    const controller = new AbortController();
    active.current = controller;
    setPending(true);
    setError(false);
    try {
      const all: WaitlistItem[] = [];
      let before: string | undefined;
      do {
        const result = await api.waitlist(
          { ...filters, ...(before ? { before } : {}) },
          controller.signal,
        );
        all.push(...result.items);
        before = result.nextCursor ?? undefined;
      } while (before && !controller.signal.aborted);
      if (!controller.signal.aborted) setItems(all);
    } catch {
      if (!controller.signal.aborted) setError(true);
    } finally {
      if (!controller.signal.aborted) setPending(false);
    }
  }, []);
  useEffect(() => {
    void load();
    return () => active.current?.abort();
  }, [load]);
  const normalizedSearch = search.trim().toLowerCase();
  const filteredItems = normalizedSearch
    ? items.filter((item) => item.email.includes(normalizedSearch))
    : items;
  const pageCount = Math.max(1, Math.ceil(filteredItems.length / PAGE_SIZE));
  const pageItems = filteredItems.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  function exportAll(format: "csv" | "excel") {
    const headers = [words.email, words.signedUpAt, words.consent];
    const rows = filteredItems.map((item) => [
      item.email,
      item.createdAt,
      item.launchUpdatesConsent ? words.yes : words.no,
    ]);
    if (format === "csv") {
      downloadCsv("launch-waitlist.csv", headers, rows);
    } else {
      downloadExcel("launch-waitlist.xls", words.sheet, headers, rows);
    }
  }
  return (
    <section className="body waitlist-inbox">
      <div className="card waitlist-toolbar">
        <button
          className="btn btn-quiet"
          type="button"
          disabled={pending}
          onClick={() => void load()}
        >
          {words.refresh}
        </button>
        <fieldset className="waitlist-toolbar-group waitlist-export" aria-label={words.export}>
          <button className="btn btn-quiet" type="button" onClick={() => exportAll("csv")}>
            {words.csv}
          </button>
          <button className="btn btn-quiet" type="button" onClick={() => exportAll("excel")}>
            {words.excel}
          </button>
        </fieldset>
        <span className="waitlist-total" aria-live="polite">
          {normalizedSearch
            ? words.matching(filteredItems.length, items.length)
            : words.total(items.length)}
        </span>
        <input
          aria-label={words.search}
          className="input waitlist-search"
          placeholder={words.searchPlaceholder}
          type="search"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
        />
      </div>
      {error ? (
        <p className="banner" role="alert">
          {words.failure}
        </p>
      ) : null}
      {pageItems.length ? (
        <div className="card waitlist-table-wrap">
          <table className="waitlist-table">
            <thead>
              <tr>
                <th>{words.email}</th>
                <th>{words.signedUp}</th>
                <th>{words.consent}</th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map((item) => (
                <tr key={item.id}>
                  <td>
                    <strong>{item.email}</strong>
                  </td>
                  <td>
                    <time dateTime={item.createdAt}>{asMoment(item.createdAt)}</time>
                  </td>
                  <td>✓</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {pending ? (
        <p role="status">{words.loading}</p>
      ) : !filteredItems.length && !error ? (
        <p className="empty">{words.empty}</p>
      ) : null}
      <nav className="card waitlist-pagination" aria-label={words.pagination}>
        <span>
          {words.page} {page} / {pageCount}
        </span>
        <button
          className="btn btn-quiet btn-sm"
          type="button"
          disabled={page === 1}
          onClick={() => setPage((current) => current - 1)}
        >
          {words.previous}
        </button>
        <button
          className="btn btn-quiet btn-sm"
          type="button"
          disabled={page === pageCount}
          onClick={() => setPage((current) => current + 1)}
        >
          {words.next}
        </button>
      </nav>
    </section>
  );
}
